import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { StaffSiteSale, StaffSiteSaleLineItem } from '../../../../../models/staff-site-sale';
import { StaffSiteSaleService } from '../../../../../services/staff-site-sale/staff-site-sale.service';
import { SelectCustomerModalComponent } from '../../modals/select-customer-modal/select-customer-modal.component';
import { Customer, CustomerProfileDetails, CustomerSearchResult } from '../../../../../models/customer';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import {
  dateStringFromISO8601,
  getDiscountDisplayName,
  propExists,
  propExistsAndIsNonEmpty,
  timeStringFromISO8601,
} from '../../../../../utils';
import {
  CreateNewCustomerModalComponent,
} from '../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';
import { EntryPass } from '../../../../../models/entry-pass';
import { Membership } from '../../../../../models/membership';
import { VoucherPack, VoucherPackStatus } from '../../../../../models/voucher-pack';
import { MembershipService } from '../../../../../services/membership/membership.service';
import { EntryPassService } from '../../../../../services/entry-pass/entry-pass.service';
import { VoucherPackService } from '../../../../../services/voucher-pack/voucher-pack.service';
import { EntryPassStatus } from '../../../../../models/entry-pass-enums';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../models/membership-enums';
import {
  AddEntryPassLineItemToSaleModalComponent,
} from './modals/add-entry-pass-line-item-to-sale-modal/add-entry-pass-line-item-to-sale-modal.component';
import { getPOSSoftwareDisplay, OrganizationConfig } from '../../../../../models/organization-config';
import { InvoiceStatus, LineItemPurchaseType } from '../../../../../models/payment-enums';
import {
  SimpleMessageModalComponent,
} from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { displayMXMerchantCancelError, initPayment } from '../../modals/utils';
import * as liveReaderRefusal from '../../modals/live-reader-refusal';
import { MarkdownRenderService } from '../../../../../services/markdown-render/markdown-render.service';
import {
  AddVoucherPackLineItemToSaleModalComponent,
} from './modals/add-voucher-pack-line-item-to-sale-modal/add-voucher-pack-line-item-to-sale-modal.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {
  AddMembershipLineItemToSaleModalComponent,
} from './modals/add-membership-line-item-to-sale-modal/add-membership-line-item-to-sale-modal.component';
import {
  SelectRegisterModalComponent,
} from './modals/select-register-modal/select-register-modal.component';
import { LightspeedRegister } from '../../../../../models/lightspeed-register';
import { CustomerService } from '../../../../../services/customer/customer.service';
import {
  CheckInAlertsModalComponent,
} from '../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-alerts-modal/check-in-alerts-modal.component';
import {
  CheckInNoEntryMethodModalComponent,
} from '../../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-no-entry-method-modal/check-in-no-entry-method-modal.component';
import { CheckInService } from '../../../../../services/check-in/check-in.service';
import { AccountService } from '../../../../../services/account/account.service';
import { OrgPOSSoftware } from '../../../../../models/organization-config-enums';


@Component({
  selector: 'app-sale-init',
  templateUrl: './sale-init.component.html',
  styleUrls: ['./sale-init.component.css']
})
export class SaleInitComponent implements OnInit, OnDestroy {
  public sale: StaffSiteSale = null;
  public discountCodes: {[key: number]: string} = {}
  public tsAndCsCheckBoxStatuses: {[key: number]: boolean} = {}

  public customers: {[key: number]: Customer} = {};
  public customerProfileDetails: {[key: number]: CustomerProfileDetails} = {};

  public orgConfig: OrganizationConfig = null;

  public errorMessage = '';
  public liveReaderRefusal: liveReaderRefusal.LiveReaderRefusal = null;
  public saleNotFound = false;
  public serverError: number = null;
  public discountCodeSuccessMessages: {[key: number]: string} = {}
  public discountCodeErrorMessages: {[key: number]: string} = {}

  public loadingSale = false;
  public loadingEntryPasses = true;
  public loadingMemberships = true;
  public loadingVoucherPacks = true;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly getDiscountDisplayName = getDiscountDisplayName;
  public readonly propExists = propExists;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;
  public readonly getPOSSoftwareDisplay = getPOSSoftwareDisplay;
  public readonly InvoiceStatus = InvoiceStatus;
  public readonly LineItemPurchaseType = LineItemPurchaseType;

  private entryPasses: EntryPass[];
  private memberships: Membership[];
  private voucherPacks: VoucherPack[];

  private refreshIntervalHolder: any = null;
  private pollingIntervalMs: number = null;

  constructor(
    public orgConfigService: OrganizationConfigService,
    public markdownRenderService: MarkdownRenderService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private staffSiteSaleService: StaffSiteSaleService,
    private entryPassService: EntryPassService,
    private membershipService: MembershipService,
    private voucherPackService: VoucherPackService,
    private customerService: CustomerService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private checkInService: CheckInService,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    displayMXMerchantCancelError(this.modalService, this.route, this.router);

    this.route.params.subscribe(routeParams => {
      this.sale = null;
      this.errorMessage = '';
      this.liveReaderRefusal = null;
      if (routeParams.hasOwnProperty('id')) {
        const saleId = parseInt(routeParams.id, 10);
        if (isNaN(saleId)) {
          this.saleNotFound = true;
          return;
        }
        this.loadExistingSale(saleId);
      } else {
        // If a specific sale is not being loaded, check if a customer ID is in the query parameters.
        const initialQPs = this.activatedRoute.snapshot.queryParamMap;
        if (initialQPs.get('customerId')) {
          const parsedCustomerId = parseInt(initialQPs.get('customerId'), 10);
          if (!isNaN(parsedCustomerId)) {
            this.setCustomer(parsedCustomerId);
          }
        }
      }
    });

    this.orgConfig = this.orgConfigService.getOrganizationConfig();
    if (!propExists(this.orgConfig)) {
      this.orgConfigService
        .loadOrganizationConfig()
        .then(() => {
          this.orgConfig = this.orgConfigService.getOrganizationConfig();
        });
    }

    this.loadEntryPasses();
    this.loadMemberships();
    this.loadVoucherPacks();
  }

  ngOnDestroy() {
    this.stopPollingForSaleUpdates();
  }

  public openSelectCustomerModal() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      this.setCustomer(result.id);
    };
  }

  public openCreateCustomerModal() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.setCustomer(result.id);
    };
  }

  public handleEditLineItem(lineItem: StaffSiteSaleLineItem) {
    this.openAddModalForLineItem(
      lineItem,
      true
    );
  }

  public handleDuplicateLineItem(lineItem: StaffSiteSaleLineItem) {
    this.openAddModalForLineItem(
      lineItem,
      false
    );
  }

  public handleRemoveLineItem(lineItem: StaffSiteSaleLineItem) {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Line Item';
    modalRef.componentInstance.messageText = 'Are you sure you want to remove this line item?';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSale = true;

      this.staffSiteSaleService
        .removeLineItem(this.sale.id, lineItem.id)
        .then(sale => { this.sale = sale; })
        .catch(reason => {
           if (reason.status === 400) {
            this.errorMessage = reason.error.useful_error_message;
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSale = false; });
    }
  }

  public handleCheckIn(lineItem: StaffSiteSaleLineItem,
                       bypassAlerts: boolean,
                       freeEntryReason? : string): Promise<void> {
    this.loadingSale = true;

    return this.customerService
      .checkInCustomer(
        lineItem.customer_id,
        bypassAlerts,
        freeEntryReason,
      )
      .then(() => {
        return this.handleSuccessfulCheckIn();
      })
      .catch(reason => {
        const errorType = reason.error.error;
        if (errorType === 'check_in_alerts') {
          const checkInAlerts = reason.error.alerts;
          const modalRef = this.modalService.open(CheckInAlertsModalComponent);
          modalRef.componentInstance.alerts = checkInAlerts;
          modalRef.componentInstance.checkInAnywayCallback = () => {
            return this.handleCheckIn(lineItem, true, freeEntryReason);
          };
        }
        else if (errorType === 'no_entry_method') {
          const modalRef = this.modalService.open(CheckInNoEntryMethodModalComponent);
          modalRef.componentInstance.customerId = lineItem.customer_id;
          modalRef.componentInstance.provideFreeEntryCallback = (thisFreeEntryReason: string) => {
            return this.handleCheckIn(lineItem, bypassAlerts, thisFreeEntryReason);
          };
        }
      })
      .finally(() => { this.loadingSale = false; });
  }

  public handleSharePass(lineItem: StaffSiteSaleLineItem,
                         customerId: number | null,
                         bypassAlerts: boolean): Promise<void> {
    if (!propExists(lineItem.number_of_uses_remaining) || (lineItem.number_of_uses_remaining <= 0)) {
      return Promise.reject('Line item has no available entries.');
    }

    if (customerId === null) {

      const modalRef = this.modalService.open(SelectCustomerModalComponent);
      modalRef.componentInstance.excludeCustomerIds = lineItem.customers_checked_in_using_product.map(cciup => cciup.id);
      modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
        return this.handleSharePass(lineItem, result.id, bypassAlerts);
      };

    } else {

      this.customerService
        .checkInCustomerViaEntryPass(
          customerId,
          lineItem.product_id,
          bypassAlerts
        )
        .then(() => {
          return this.handleSuccessfulCheckIn();
        })
        .catch(reason => {
          this.loadingSale = true;
          const errorType = reason.error.error;
          if (errorType === 'check_in_alerts') {
            const checkInAlerts = reason.error.alerts;
            const modalRef = this.modalService.open(CheckInAlertsModalComponent);
            modalRef.componentInstance.alerts = checkInAlerts;
            modalRef.componentInstance.checkInAnywayCallback = () => {
              return this.handleSharePass(lineItem, customerId, true);
            };
          }
        })
        .finally(() => { this.loadingSale = false; });

    }
  }

  public openAddEntryPassModal() {
    if (this.loadingEntryPasses) { return; }

    const modalRef = this.modalService.open(
      AddEntryPassLineItemToSaleModalComponent
    );
    modalRef.componentInstance.sale = this.sale;
    modalRef.componentInstance.entryPasses = this.entryPasses;
    modalRef.componentInstance.savedCallback = (result: StaffSiteSale) => {
      this.sale = result;
    };
  }

  public openAddMembershipModal() {
    if (this.loadingVoucherPacks) { return; }

    const modalRef = this.modalService.open(
      AddMembershipLineItemToSaleModalComponent
    );
    modalRef.componentInstance.sale = this.sale;
    modalRef.componentInstance.memberships = this.memberships;
    modalRef.componentInstance.savedCallback = (result: StaffSiteSale) => {
      this.sale = result;
    };
  }

  public openAddVoucherPackModal() {
    if (this.loadingVoucherPacks) { return; }

    const modalRef = this.modalService.open(
      AddVoucherPackLineItemToSaleModalComponent
    );
    modalRef.componentInstance.sale = this.sale;
    modalRef.componentInstance.voucherPacks = this.voucherPacks;
    modalRef.componentInstance.savedCallback = (result: StaffSiteSale) => {
      this.sale = result;
    };
  }

  public loadingProductTypes() {
    return (
      this.loadingEntryPasses ||
      this.loadingMemberships ||
      this.loadingVoucherPacks
    );
  }

  public handleCancelSale() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Cancel Sale';
    modalRef.componentInstance.messageText = 'Are you sure you want to cancel this sale?';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.staffSiteSaleService.cancelStaffSiteSale(this.sale.id);
      location.href = '/sale';
    }
  }

  public handleCompletePurchase() {
    initPayment(
      this.modalService,
      this.sale.id,
      this.sale.payment_processor_type,
      this.sale.customer_id,
      'Capitan Staff Site sale',
      location.href,
      location.href,
      () => {
        this.loadExistingSale(this.sale.id);
      },
      undefined,
      () => {
        this.loadExistingSale(this.sale.id);
      }
    )
  }

  public canCompletePurchase(): boolean {
    if (!propExists(this.sale)) { return false; }

    if (this.loadingSale) { return false; }

    for (const lineItem of this.sale.line_items) {
      if (lineItem.terms_and_conditions_checkbox_text !== '') {
        if (!this.tsAndCsCheckBoxStatuses[lineItem.id]) {
          return false;
        }
      }
    }

    return true;
  }

  public canSendToPOS(): boolean {
    return (
      !this.loadingSale &&
      this.canCompletePurchase() &&
      this.orgConfig &&
      (this.orgConfig.pos_software === OrgPOSSoftware.lightspeed)
    );
  }

  // Why the register is refused: see create_pos_sale_for_staff_site_sale in capitan-server.
  public sendToPOSTooltip(): string | null {
    if (!this.saleHasPayments()) { return null; }
    return 'This sale has account credit applied and cannot be sent to Lightspeed. ' +
           'Collect the remaining balance in Capitan.';
  }

  public handleDiscountCodeSubmit(lineItemId: number) {
    this.discountCodeSuccessMessages = {};
    this.discountCodeErrorMessages = {};

    this.loadingSale = true;

    this.staffSiteSaleService
      .applyDiscountCodeToLineItem(
        this.sale.id,
        lineItemId,
        this.discountCodes[lineItemId],
      )
      .then(result => {
        this.discountCodes = {};
        this.sale = result.sale;
        this.discountCodeSuccessMessages[result.line_item_id] = result.text;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.saleNotFound = true;
        } else if (reason.status === 400) {
          this.discountCodeErrorMessages[lineItemId] = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSale = false; });
  }

  public handleSendToPOS() {
    if (this.orgConfig === null) { return; }

    const modalRef = this.modalService.open(SelectRegisterModalComponent);
    modalRef.componentInstance.saleId = this.sale.id;
    modalRef.componentInstance.submitCallback = (register: LightspeedRegister | null) => {
      this.sendToPOS(register);
    }
  }

  private sendToPOS(register: LightspeedRegister | null,
                    options: liveReaderRefusal.LiveReaderActionOptions = {},
                    saleId: number = this.sale.id
                    ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    // A recheck or bypass from a refusal must not send a different sale opened since.
    if (this.sale?.id !== saleId) {
      this.liveReaderRefusal = null;
      return Promise.resolve('other');
    }

    if (!options.recheck) { this.liveReaderRefusal = null; }
    this.loadingSale = true;

    return this.staffSiteSaleService
      .sendStaffSiteSaleToPos(saleId, register?.id, options.bypass)
      .then((sale): liveReaderRefusal.LiveReaderCheckOutcome => {
        this.sale = sale;
        this.startPollingForSaleUpdates(3000);
        window.open(
          this.sale.lightspeed_created_sale_link,
          '_blank'
        );
        return 'cleared';
      })
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome | Promise<liveReaderRefusal.LiveReaderCheckOutcome> => {
        if (reason.status === 404) {
          this.saleNotFound = true;
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          if (options.recheck) { return 'live'; }
          const refusal: liveReaderRefusal.LiveReaderRefusal = {
            message: liveReaderRefusal.getErrorMessageText(reason),
            checkAgain: () => this.sendToPOS(register, { recheck: true }, saleId),
            checking: false,
          };
          liveReaderRefusal.openProceedAnywayModal(
            this.modalService,
            refusal,
            () => this.sendToPOS(register, { bypass: true }, saleId),
            () => {
              if (this.sale?.id === saleId) { this.liveReaderRefusal = refusal; }
            }
          );
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          this.errorMessage = liveReaderRefusal.getErrorMessageText(reason);
          return this.loadExistingSale(saleId).then((): liveReaderRefusal.LiveReaderCheckOutcome => 'other');
        } else if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      })
      .then(outcome => {
        if (outcome !== 'live') { this.liveReaderRefusal = null; }
        return outcome;
      })
      .finally(() => { this.loadingSale = false; });
  }

  public handleCancelPOS() {
    if (this.orgConfig === null) { return; }

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Cancel ' + getPOSSoftwareDisplay(this.orgConfig.pos_software) + ' Sale';
    modalRef.componentInstance.messageText = 'Lightspeed does not allow sales to be cancelled automatically by ' +
      'Capitan. Please cancel the sale in the Lightspeed App ("More actions" -> "Discard sale"), before continuing.';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSale = true;

      this.staffSiteSaleService
        .cancelPosSale(this.sale.id)
        .then(sale => {
          this.sale = sale;
          this.stopPollingForSaleUpdates();
        })
        .catch(reason => {
          if (reason.status === 404) {
            this.saleNotFound = true;
          } else if (reason.status === 400) {
            this.errorMessage = reason.error.useful_error_message;
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSale = false; });
    }
  }

  public customerDetailsLoaded(customerId: number): boolean {
    return (
      propExists(this.customers[customerId]) &&
      propExists(this.customerProfileDetails[customerId])
    );
  }

  public openNewSale() {
    this.sale = null;
    this.errorMessage = '';
    this.liveReaderRefusal = null;
    this.location.go('/sale');
  }

  public saleCanBeEdited(): boolean {
    return (
      (this.sale.status === InvoiceStatus.paymentPending) &&
      (this.sale.lightspeed_created_sale_id === '')
    );
  }

  public saleIsLocked(): boolean {
    return propExists(this.sale) && propExists(this.sale.pending_payment);
  }

  public saleHasPayments(): boolean {
    return propExists(this.sale) && this.sale.payments.length > 0;
  }

  // Once credit is applied the sale's line items, discounts and customer are frozen
  // (the server refuses edits too); only paying the remainder or cancelling is left.
  public saleCannotBeChanged(): boolean {
    return this.saleIsLocked() || this.saleHasPayments();
  }

  public creditAppliedTotal(): number {
    if (!propExists(this.sale)) { return 0; }
    return this.sale.payments.reduce((total, p) => total + Number(p.amount), 0);
  }

  public handleDiscardPendingPayment() {
    const amountDisplay = this.orgConfigService.formatCurrency(this.sale.pending_payment.amount);

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Discard Pending Payment';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.messageText = (
      'The pending payment of ' + amountDisplay + ' will be cancelled with the payment processor where ' +
      'possible and treated as not taken. Before sending a new payment request, check that the card reader ' +
      'is not still showing this sale, and clear it if it is. If the reader still captures this payment, it ' +
      'will be recorded against this sale.'
    );
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingSale = true;

      this.staffSiteSaleService
        .discardPendingPayment(this.sale.id)
        .then(sale => { this.sale = sale; })
        .catch(reason => {
          if (reason.status === 404) {
            this.saleNotFound = true;
          } else if (reason.status === 400) {
            this.errorMessage = reason.error.useful_error_message;
            return this.loadExistingSale(this.sale.id);
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSale = false; });
    }
  }

  private loadExistingSale(saleId: number, silent = false): Promise<void> {
    if (!silent) {
      this.loadingSale = true;
    }

    return this.staffSiteSaleService
      .getStaffSiteSale(saleId)
      .then(sale => {
        // Not on a same-sale refresh (the silent poll), or an inline alert could vanish mid-recheck.
        if (sale.id !== this.sale?.id) { this.liveReaderRefusal = null; }
        this.sale = sale;
        this.loadCustomerDetails(this.sale.customer_id);
        for (const lineItem of this.sale.line_items) {
          if (propExists(lineItem.customer_id)) {
            this.loadCustomerDetails(lineItem.customer_id);
          }
        }

        const isLightspeedSale = this.sale.lightspeed_created_sale_id !== '';
        const hasPendingPayment = propExists(this.sale.pending_payment);
        const mustPollForUpdates = (
          (this.sale.status === InvoiceStatus.paymentPending) &&
          (isLightspeedSale || hasPendingPayment)
        );
        if (mustPollForUpdates) {
          // A pending MX sale polls a live MX Merchant API call server-side each tick, so it polls slower than Lightspeed.
          this.startPollingForSaleUpdates(isLightspeedSale ? 3000 : 10000);
        } else {
          this.stopPollingForSaleUpdates();
        }
      })
      .catch(reason => {
        if (silent) {
          // A transient failure during a background poll shouldn't blank the page the customer is looking at.
          if (reason.status === 404) {
            this.saleNotFound = true;
            this.stopPollingForSaleUpdates();
          }
          return;
        }
        if (reason.status === 404) {
          this.saleNotFound = true;
        } else if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (!silent) {
          this.loadingSale = false;
        }
      });
  }

  private loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(
        1,
        100,
        {status: EntryPassStatus.active}
      )
      .then(result => { this.entryPasses = result.results; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        100,
        {
          status: MembershipStatus.active,
          purchase_type: MembershipPurchaseType.prepaid,
        }
      )
      .then(result => {
        this.memberships = result.results.filter(m => !m.is_group);
      })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingMemberships = false; });
  }

  private loadVoucherPacks() {
    this.loadingVoucherPacks = true;

    this.voucherPackService
      .getVoucherPacks(
        1,
        100,
        {status: VoucherPackStatus.active}
      )
      .then(result => { this.voucherPacks = result.results; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingVoucherPacks = false; });
  }

  private setCustomer(customerId: number) {
    this.loadingSale = true;

    let salePromise: Promise<StaffSiteSale>;
    if (this.sale === null) {
      salePromise = this.staffSiteSaleService
        .createStaffSiteSale(customerId);
    } else {
      salePromise = this.staffSiteSaleService
        .updateStaffSiteSale(this.sale.id, customerId);
    }

    salePromise
      .then(sale => {
        this.sale = sale;
        this.liveReaderRefusal = null;
        this.location.replaceState('/sale/' + this.sale.id.toString());
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.saleNotFound = true;
        } else if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSale = false; });

    this.loadCustomerDetails(customerId);
  }

  private openAddModalForLineItem(lineItem: StaffSiteSaleLineItem,
                                  replaceExistingLineItem: boolean) {
    let modalRef: NgbModalRef;

    switch (lineItem.purchase_type) {
      case LineItemPurchaseType.entryPass:
        modalRef = this.modalService.open(
          AddEntryPassLineItemToSaleModalComponent
        );
        modalRef.componentInstance.entryPasses = this.entryPasses;
        break;
      case LineItemPurchaseType.membershipInitial:
        modalRef = this.modalService.open(
          AddMembershipLineItemToSaleModalComponent
        );
        modalRef.componentInstance.memberships = this.memberships;
        break;
      case LineItemPurchaseType.voucherPack:
        modalRef = this.modalService.open(
          AddVoucherPackLineItemToSaleModalComponent
        );
        modalRef.componentInstance.voucherPacks = this.voucherPacks;
        break;
      default:
        return;
    }

    modalRef.componentInstance.sale = this.sale;
    modalRef.componentInstance.lineItemToCopy = lineItem;
    modalRef.componentInstance.replaceExistingLineItem = replaceExistingLineItem;
    modalRef.componentInstance.savedCallback = (result: StaffSiteSale) => {
      this.sale = result;
      for (const saleLineItem of this.sale.line_items) {
        if (propExists(lineItem.customer_id)) {
          this.loadCustomerDetails(saleLineItem.customer_id);
        }
      }
    };
  }

  private loadCustomerDetails(customerId: number) {
    if (!propExists(customerId)) { return; }

    if (this.customerDetailsLoaded(customerId)) { return; }

    this.customerService
      .getCustomer(customerId)
      .then(customer => {
        this.customers[customerId] = customer;
      })
      .catch(reason => {
        this.serverError = reason.status;
      });

    this.customerService
      .getCustomerProfileDetails(customerId)
      .then(customerProfileDetials => {
        this.customerProfileDetails[customerId] = customerProfileDetials;
      })
      .catch(reason => {
        this.serverError = reason.status;
      });
  }

  private handleSuccessfulCheckIn(): Promise<void> {
    // Make requests to load the check-in stats and facility check-ins in
    // order to refresh the cache.
    this.checkInService.getCheckInStats(
      this.accountService.getMyAccount().default_gym_id,
      true
    ).then();
    this.checkInService.getInFacilityCustomers(
      this.accountService.getMyAccount().default_gym_id,
      true
    ).then();

    return this.loadExistingSale(this.sale.id);
  }

  private startPollingForSaleUpdates(intervalMs: number) {
    if (this.refreshIntervalHolder !== null) {
      if (this.pollingIntervalMs === intervalMs) {
        return;
      }
      this.stopPollingForSaleUpdates();
    }
    this.pollingIntervalMs = intervalMs;
    this.refreshIntervalHolder = setInterval(
      () => {
        this.loadExistingSale(this.sale.id, true);
      },
      intervalMs
    );
  }

  private stopPollingForSaleUpdates() {
    if (this.refreshIntervalHolder !== null) {
      clearInterval(this.refreshIntervalHolder);
      this.refreshIntervalHolder = null;
      this.pollingIntervalMs = null;
    }
  }
}
